import './fixed-submit-bar.scss';
import {observer} from "mobx-react";
import {BUTTON_VARIANTS} from "../../../components/inputs/button-variants";
import {Button} from "../../../components/inputs/button/button";
import {LABEL_POSITIONS, Toggle} from "../../../components/inputs/toggle/toggle";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import {CreateRmaMainStore} from "../stores/create-rma-main-store";
import {IValidationGroup} from '../../../hooks/validation-group/validation-group-hook';
import {Tooltip, TOOLTIP_VARIANTS} from "../../../components/tooltip/tooltip";
import FontBody2 from "../../../components/fonts/font-body2/font-body2";
import {FONT_COLOR} from "../../../components/fonts/font-props";
import {TOOLTIP_PLACEMENTS} from "../../../components/tooltip/tooltip-box";
import {
    getManagersApprovalReasonTranslationKeys, IManagersApprovalTranslations,
    MANAGERS_APPROVAL_REASONS
} from "../../manage-returns/rma-page/rma-types";
import currentAccountStore from '../../accounts/stores/current-account-store';
import currentPortalStore from '../../../stores/current-portal-store';
import { PERMISSION } from '../../../models/PermissionsTypes';
import { CREATE_RMA_MODE } from '../../../services/api/create-rma-service';
import BaseLayoutStickyToolbar
    from "../../../components/layouts/base-page-layout/base-layout-sticky-toolbar/base-layout-sticky-toolbar";


interface IProps {
    createRmaMainStore: CreateRmaMainStore;
    validtionGroup: IValidationGroup;
}

const ButtonsGodMode: React.FC<IProps> = observer(({ createRmaMainStore, validtionGroup }) => {
    const isCreatingRma = createRmaMainStore.isCreatingRma;
    const rmaRequestStore = createRmaMainStore.rmaRequestStore;
    const orderStore = createRmaMainStore.orderStore;
    const pageScope = 'dashboard-create-rma';
    const { getTranslation } = useDashboardTranslation();
    const translationKeys = getManagersApprovalReasonTranslationKeys(MANAGERS_APPROVAL_REASONS.OUT_OF_POLICY) as IManagersApprovalTranslations;

    const shouldShowDisabledButtonWithTooltip = (): boolean => {
        /** when the request is not yet constructed - we cannot say anything yet about disabling button/eligibility */
        if (rmaRequestStore?.requestProducts.length === 0) {
            return false;
        }

        /** when "General Returns" setting "Require Manager Approval" is OFF */
        if (!currentPortalStore.requireManagerApproval()) {
            return false;
        }

        /** if request is eligible - do not disable */
        if (!(rmaRequestStore?.eligibilityStore.isOutOfPolicy ?? false)) {
            return false;
        }

        /** disable for users without permissions */
        if (currentAccountStore.hasPermission(PERMISSION.APPROVE_RMA_AS_MANAGER) || currentAccountStore.hasPermission(PERMISSION.PROMPT_MANGER_APPROVAL)) {
            return false;
        }

        return true;
    }

    const disableSubmitButton = rmaRequestStore?.requestProducts.length === 0 || isCreatingRma || !validtionGroup.isValid() || createRmaMainStore.rmaRequestStore?.eligibilityStore.isInProgress || orderStore?.isCreateRmaNotAllowed;

    return (
        <span className={'fixed-submit-bar-margin-left'}>
            <Button variant={BUTTON_VARIANTS.PrimaryAccent}
                text={getTranslation('SUBMIT_RMA_AS_PENDING', pageScope)}
                disabled={disableSubmitButton}
                hasLoader={true}
                isLoading={isCreatingRma && !createRmaMainStore.rmaRequestStore?.isAutoApprove}
                onClick={() => {
                    createRmaMainStore.rmaRequestStore?.setIsAutoApprove(false);
                    createRmaMainStore.createRma();
                }}
            />

            { shouldShowDisabledButtonWithTooltip() ?
                <Tooltip placement={TOOLTIP_PLACEMENTS.LEFT} variant={TOOLTIP_VARIANTS.TEXT} content={
                    <FontBody2 color={FONT_COLOR.PRIMARY3_50} >
                        {translationKeys?.tooltipKey?.key && translationKeys?.tooltipKey?.scope &&
                            getTranslation(translationKeys.tooltipKey.key, translationKeys.tooltipKey.scope)}
                    </FontBody2>}>
                    <Button variant={BUTTON_VARIANTS.PrimaryAccent}
                            text={getTranslation('SUBMIT_RMA_AS_APPROVED', pageScope)}
                            disabled={true}
                            hasLoader={true}
                            onClick={() => {}}
                    />
                </Tooltip>
                :
                <Button variant={BUTTON_VARIANTS.PrimaryAccent}
                        text={getTranslation('SUBMIT_RMA_AS_APPROVED', pageScope)}
                        disabled={disableSubmitButton}
                        hasLoader={true}
                        isLoading={isCreatingRma && createRmaMainStore.rmaRequestStore?.isAutoApprove}
                        onClick={() => {
                            createRmaMainStore.rmaRequestStore?.setIsAutoApprove(true);
                            createRmaMainStore.createRma();
                        }}
                />
            }
        </span>
    );
});

const ButtonPolicyLed: React.FC<IProps> = observer(({ createRmaMainStore, validtionGroup }) => {
    const isCreatingRma = createRmaMainStore.isCreatingRma;
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';
    const rmaRequestStore = createRmaMainStore.rmaRequestStore;
    const shouldDisableSubmitButton = rmaRequestStore?.requestProducts.length === 0 || isCreatingRma 
        || !validtionGroup.isValid() || createRmaMainStore.rmaRequestStore?.eligibilityStore.isOutOfPolicy 
        || createRmaMainStore.rmaRequestStore?.eligibilityStore.isInProgress || createRmaMainStore.orderStore?.isCreateRmaNotAllowed;
    
    return (
        <span className={'fixed-submit-bar-margin-left'}>
            <Button variant={BUTTON_VARIANTS.PrimaryAccent}
                text={getTranslation('SUBMIT_RMA', pageScope)}
                disabled={shouldDisableSubmitButton}
                hasLoader={true}
                isLoading={isCreatingRma && !createRmaMainStore.rmaRequestStore?.isAutoApprove}
                onClick={() => {
                    createRmaMainStore.rmaRequestStore?.setIsAutoApprove(false);
                    createRmaMainStore.createRma();
                }}
            />
        </span>
    );
});


const FixedSubmitBar: React.FC<IProps> = observer(({ createRmaMainStore, validtionGroup }) => {
	const rmaRequestStore = createRmaMainStore.rmaRequestStore;
	const isCustomerEmailMissing = !createRmaMainStore.orderStore?.customer?.email;

    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';

	return (
		<BaseLayoutStickyToolbar>
            <span>
                <Toggle label={getTranslation('SEND_CUSTOMER_EMAILS', pageScope)}
                        checked={rmaRequestStore?.sendCustomerEmails}
                        onChange={rmaRequestStore?.setSendCustomerEmail}
                        labelPosition={LABEL_POSITIONS.LEFT}
                        disabled={isCustomerEmailMissing}/>
            </span>
            
            { createRmaMainStore.settings.cRmaMode === CREATE_RMA_MODE.C_RMA_GOD_MODE && 
                <ButtonsGodMode createRmaMainStore={createRmaMainStore} validtionGroup={validtionGroup} /> 
            }
            { createRmaMainStore.settings.cRmaMode === CREATE_RMA_MODE.C_RMA_POLICY_LED_MODE && 
                <ButtonPolicyLed createRmaMainStore={createRmaMainStore} validtionGroup={validtionGroup} /> 
            }

        </BaseLayoutStickyToolbar>
	);
});

export default FixedSubmitBar;
